// "The table" — the comment thread under a Reader document (DECISIONS.md
// 2026-07-05). Talk is free: at depth 0 this is just a thread. A comment vote
// is a vote on its attachment edge (▲/▼ toggle — sorts the table, never
// enters a lens). A reply may carry a typed epistemic edge (rebuts /
// supports / asks); those render as votable EdgeSentences from depth 1 up.

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from './config';
import EdgeSentence, { castVote } from './EdgeSentence';
import { Explain } from './Explain';

const REPLY_TYPES = [
  { value: '', label: 'just talk' },
  { value: 'contradicts', label: '⊗ rebuts' },
  { value: 'supports', label: '✓ supports' },
  { value: 'asks', label: '? asks' },
];

function Composer({ placeholder, onSubmit, withType, busy }) {
  const [text, setText] = useState('');
  const [type, setType] = useState('');
  return (
    <div className="rt-composer">
      <textarea
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        aria-label={placeholder}
      />
      <div className="rt-composer-row">
        {withType && (
          <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Reply type">
            {REPLY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        )}
        <button
          className="rt-post"
          disabled={busy || !text.trim()}
          onClick={async () => {
            await onSubmit(text.trim(), type || null);
            setText('');
            setType('');
          }}
        >
          Post
        </button>
        <span className="rt-hint"><Explain concept="the-table" /></span>
      </div>
    </div>
  );
}

function Comment({ node, user, onReply, onVoted, onStake, busy, parentContent }) {
  const [replying, setReplying] = useState(false);
  const [staking, setStaking] = useState(false);
  const e = node.reply_edge;
  const stake = async () => {
    setStaking(true);
    try {
      await onStake(node.id);
    } finally {
      setStaking(false);
    }
  };
  const karma = async (polarity) => {
    // a comment vote IS a vote on its attachment edge (toggle = withdraw)
    await castVote({ id: e.id, my_vote: e.my_vote }, polarity);
    onVoted();
  };
  return (
    <div className="rt-comment">
      <div className="rt-meta">
        <b>{node.author || 'unknown'}</b>
        <span>{new Date(node.created_at).toLocaleString()}</span>
        {user ? (
          <span className="rt-karma">
            <button className={e.my_vote === 1 ? 'on' : ''} onClick={() => karma(1)} aria-label="upvote">▲</button>
            <i>{e.vote_score}</i>
            <button className={e.my_vote === -1 ? 'on' : ''} onClick={() => karma(-1)} aria-label="downvote">▼</button>
          </span>
        ) : (
          <span className="rt-karma"><i>{e.vote_score}</i></span>
        )}
        {node.model_access === 'sealed' && <span className="rt-sealed">sealed</span>}
        {user && (
          <button className="rt-replybtn" onClick={() => setReplying(!replying)}>
            {replying ? 'cancel' : 'reply'}
          </button>
        )}
        {user && node.staked_claims.length === 0 && (
          <>
            <button
              className="rt-stakebtn"
              disabled={staking}
              title="Extract this comment's claims into the standing argument"
              onClick={stake}
            >
              {staking ? 'staking…' : '⚖ stake'}
            </button>
            <Explain concept="staking" />
          </>
        )}
      </div>
      <p className="rt-say">{node.content}</p>

      {node.staked_claims.length > 0 && (
        <div className="rt-staked">
          <span className="rt-staked-k">
            <Explain concept="staking">
              ⚖ staked · {node.staked_claims.length} claim{node.staked_claims.length === 1 ? '' : 's'} in the graph
            </Explain>
          </span>
          {node.staked_claims.map((c) => (
            <Link key={c.id} to={`/object/${c.id}`} className="rt-staked-chip">
              “{c.content.length > 90 ? c.content.slice(0, 90) + '…' : c.content}”
            </Link>
          ))}
        </div>
      )}

      {node.typed_edges.length > 0 && (
        <div className="rt-typed">
          {node.typed_edges.map((t) => (
            <EdgeSentence
              key={t.id}
              edge={{
                ...t,
                source_id: t.source,
                source_content: node.content,
                target_id: t.target,
                target_content: parentContent || '',
              }}
              focusId={t.source}
              user={user}
              onVoted={onVoted}
            />
          ))}
        </div>
      )}

      {replying && (
        <Composer
          placeholder={`Reply to ${node.author || 'them'}…`}
          withType
          busy={busy}
          onSubmit={async (text, type) => {
            await onReply(node.id, text, type);
            setReplying(false);
          }}
        />
      )}

      {node.children.length > 0 && (
        <div className="rt-children">
          {node.children.map((c) => (
            <Comment
              key={c.id}
              node={c}
              user={user}
              onReply={onReply}
              onVoted={onVoted}
              onStake={onStake}
              busy={busy}
              parentContent={node.content}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReadTable({ documentId, user, onGraphChange }) {
  const [thread, setThread] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await apiFetch(`/api/comments/${documentId}`);
    if (res.ok) setThread(await res.json());
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  const post = async (body) => {
    setBusy(true);
    try {
      const res = await apiFetch('/api/comments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        await load();
        // a typed reply added a trust-bearing edge — the article view cares
        if (body.reply_type && onGraphChange) onGraphChange();
      }
    } finally {
      setBusy(false);
    }
  };

  if (!thread) return null;

  return (
    <section className="rt-table">
      <div className="rt-head">
        <h2><Explain concept="the-table">The table</Explain></h2>
        <span>
          {thread.count} comment{thread.count === 1 ? '' : 's'}
        </span>
      </div>

      {user ? (
        <Composer
          placeholder="Say something at the table…"
          busy={busy}
          onSubmit={(text) => post({ document_id: documentId, content: text })}
        />
      ) : (
        <p className="rt-hint">Log in to join the table.</p>
      )}

      {thread.comments.map((c) => (
        <Comment
          key={c.id}
          node={c}
          user={user}
          busy={busy}
          onVoted={load}
          onStake={async (commentId) => {
            const res = await apiFetch(`/api/comments/${commentId}/stake`, { method: 'POST' });
            if (res.ok) {
              await load();
              // staking may have reused an article claim — the page above cares
              if (onGraphChange) onGraphChange();
            }
          }}
          onReply={(parentId, text, type) =>
            post({ parent_comment_id: parentId, content: text, reply_type: type })
          }
        />
      ))}
    </section>
  );
}
